import { Autocomplete, Box, Button, Card, FormControl, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import UnauthorizedPage from '../../../UnAuthorized';
import { WhatsappEnumServices } from './service';
import { WhatsappEnumDataContext } from './WhatsappEnum';

// Helper function to transform notification enum
const transformNotificationEnum = (value) => {
    if (!value) return value;
    return value
        .toUpperCase()
        .replace(/\s+/g, '_')           // Replace spaces with underscores
        .replace(/_+/g, '_')            // Replace multiple underscores with single
        .replace(/[^A-Z0-9_]/g, '');    // Remove any remaining special characters (keep underscores)
};

// Helper function to convert body text to simple plain text
const normalizeToPlainText = (text) => {
    if (!text) return text;
    return text
        .replace(/\n/g, ' ')            // Replace newlines with space
        .replace(/\r/g, ' ')            // Replace carriage returns with space
        .replace(/\t/g, ' ')            // Replace tabs with space
        .replace(/\s+/g, ' ')           // Replace multiple spaces with single space
        .trim();                        // Remove leading/trailing spaces
};

const AddEditForm = () => {
    const { 
        whatsappEnumInitialData, 
        whatsappEnumData, 
        setWhatsappEnumData, 
        fetchWhatsappEnumist, 
        selectedWhatsappEnumData, 
        setSelectedWhatsappEnumData,
        whatsappTemplateList 
    } = useContext(WhatsappEnumDataContext);
    
    const [pageTitle, setPageTitle] = useState('Add Whatsapp Enum');
    const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue, getValues, trigger } = useForm({
        mode: 'onChange',
        defaultValues: whatsappEnumData,
    });
    
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const isEditingRef = useRef(false);
    
    // Watch for template changes
    const watchedTemplate = watch('whatsappTemplate');
    
    // Handle template selection
    useEffect(() => {
        // Skip if we're in the middle of loading edit data
        if (isEditingRef.current) {
            return;
        }
        
        if (watchedTemplate && whatsappTemplateList.length > 0) {
            const template = whatsappTemplateList.find(t => t.name === watchedTemplate);
            if (template) {
                setSelectedTemplate(template);
                
                // Get body text from template and replace variables with example values
                const bodyComponent = template.components?.find(c => c.type === 'BODY');
                if (bodyComponent && bodyComponent.text) {
                    let bodyText = bodyComponent.text;
                    
                    // Get example values if available
                    const exampleValues = bodyComponent.example?.body_text?.[0] || [];
                    
                    // Replace {{1}}, {{2}}, etc. with example values
                    exampleValues.forEach((value, index) => {
                        const placeholder = `{{${index + 1}}}`;
                        bodyText = bodyText.replace(new RegExp(placeholder, 'g'), value);
                    });
                    
                    // Remove any remaining {{}} placeholders that weren't replaced
                    bodyText = bodyText.replace(/\{\{\d+\}\}/g, '');
                    
                    setValue('body', normalizeToPlainText(bodyText), { shouldValidate: true });
                }
                
                // Check if template has header media
                const headerComponent = template.components?.find(c => c.type === 'HEADER');
                if (headerComponent && (headerComponent.format === 'VIDEO' || headerComponent.format === 'IMAGE')) {
                    // Template has header media - set format but keep URL empty for new entries
                    setValue('headerMedia', {
                        format: headerComponent.format,
                        url: ''
                    });
                } else {
                    // No header media in template
                    setValue('headerMedia', null);
                }
            }
        } else {
            setSelectedTemplate(null);
            setValue('body', '', { shouldValidate: true });
            setValue('headerMedia', null);
        }
    }, [watchedTemplate, whatsappTemplateList, setValue]);
    
    const onSubmit = async (data) => {
        setIsSubmitted(true);
        
        // Build submission data with transformed notificationEnum and normalized body
        const submissionData = {
            title: data.title,
            body: normalizeToPlainText(data.body || ''),
            type: 'WHATSAPP',
            notificationEnum: transformNotificationEnum(data.notificationEnum),
            whatsappTemplate: data.whatsappTemplate || '',
            headerMedia: data.headerMedia || null
        };
        
        try {
            if (selectedWhatsappEnumData) {
                // Update
                const updateresponse = await WhatsappEnumServices.updateWhatsappEnum(submissionData, selectedWhatsappEnumData.id);
                if (updateresponse.success == '1') {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Data updated successfully',
                    });
                    // Clear form and template selection
                    reset(whatsappEnumInitialData);
                    setSelectedWhatsappEnumData(null);
                    setWhatsappEnumData(whatsappEnumInitialData);
                    setSelectedTemplate(null);
                    setPageTitle('Add Whatsapp Enum');
                    // Refresh list
                    fetchWhatsappEnumist();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: updateresponse.message || 'Failed to update data',
                    });
                }
            } else {
                // Save
                const saveresponse = await WhatsappEnumServices.addWhatsappEnum(submissionData);
                if (saveresponse.success == '1') {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Data saved successfully',
                    });
                    // Clear form and template selection
                    reset(whatsappEnumInitialData);
                    setSelectedTemplate(null);
                    // Refresh list
                    fetchWhatsappEnumist();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: saveresponse.message || 'Failed to save data',
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Failed to save data... Something went wrong!',
            });
        } finally {
            setIsSubmitted(false);
        }
    };
    
    useEffect(() => {
        if (selectedWhatsappEnumData) {
            setPageTitle('Edit Whatsapp Enum');
            
            // Set editing flag to prevent watch effect from overwriting data
            isEditingRef.current = true;
            
            // Pre-select template if exists
            let template = null;
            if (selectedWhatsappEnumData.whatsappTemplate) {
                template = whatsappTemplateList.find(t => t.name === selectedWhatsappEnumData.whatsappTemplate);
                setSelectedTemplate(template || null);
            }
            
            // Ensure headerMedia structure is correct
            const formData = {
                ...selectedWhatsappEnumData,
                headerMedia: selectedWhatsappEnumData.headerMedia || null
            };
            
            setWhatsappEnumData(formData);
            reset(formData, { isDirty: false });
            
            // Clear editing flag after a short delay to allow reset to complete
            setTimeout(() => {
                isEditingRef.current = false;
            }, 100);
        }
    }, [selectedWhatsappEnumData, whatsappTemplateList, reset, setWhatsappEnumData]);
    
    const Submitting = () => {
        Swal.fire({
            title: 'Please wait...',
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
    };
    
    const resetFormField = () => {
        reset(whatsappEnumInitialData);
        setSelectedWhatsappEnumData(null);
        setWhatsappEnumData(whatsappEnumInitialData);
        setSelectedTemplate(null);
        isEditingRef.current = false;
        setPageTitle('Add Whatsapp Enum');
    };
    
    // Check if selected template requires header media
    const requiresHeaderMedia = selectedTemplate?.components?.some(
        c => c.type === 'HEADER' && (c.format === 'VIDEO' || c.format === 'IMAGE')
    );
    
    const headerMediaFormat = selectedTemplate?.components?.find(
        c => c.type === 'HEADER' && (c.format === 'VIDEO' || c.format === 'IMAGE')
    )?.format;
    
    return (
        <PermissionGuard feature="notification" permission={selectedWhatsappEnumData ? 'canEdit' : "canCreate"} fallback={selectedWhatsappEnumData && <UnauthorizedPage />}>
            <Card>
                {isSubmitted && Submitting()}
                <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{pageTitle}</Typography>
                </Box>
                <Box component={'form'} onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
                    <Grid container spacing={1}>
                        {/* Title Field */}
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Title <Box component='span' sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name='title'
                                control={control}
                                rules={{ required: 'Title is required' }}
                                render={({ field, fieldState: { error } }) =>
                                    <TextField
                                        {...field}
                                        size='small'
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                        inputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                }
                            />
                        </Grid>
                        
                        {/* Notification Enum Field */}
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Notification Enum <Box component='span' sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name='notificationEnum'
                                control={control}
                                rules={{ 
                                    required: 'Notification enum is required',
                                    pattern: {
                                        value: /^(?!.*__)[A-Z0-9_]+$/,
                                        message: 'Only uppercase letters, numbers, and underscores. No double underscores allowed.'
                                    }
                                }}
                                render={({ field, fieldState: { error } }) =>
                                    <TextField
                                        {...field}
                                        size='small'
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                        placeholder="EXAMPLE_NOTIFICATION_ENUM"
                                        inputProps={{ 
                                            style: { fontSize: '0.75rem', textTransform: 'uppercase' }
                                        }}
                                        onChange={(e) => {
                                            // Transform on input: remove spaces, special chars, uppercase
                                            const transformed = transformNotificationEnum(e.target.value);
                                            field.onChange(transformed);
                                        }}
                                    />
                                }
                            />
                        </Grid>                     
                                 
                        {/* Whatsapp Template Dropdown */}
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Whatsapp Template <Box component='span' sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name='whatsappTemplate'
                                control={control}
                                rules={{ required: 'Whatsapp template is required' }}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        options={whatsappTemplateList}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option?.name === value || option === value}
                                        value={whatsappTemplateList.find(t => t.name === field.value) || null}
                                        onChange={(_, newValue) => {
                                            field.onChange(newValue?.name || '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size='small'
                                                fullWidth
                                                error={!!error}
                                                helperText={error?.message}
                                                placeholder="Select a template"
                                                inputProps={{ ...params.inputProps, style: { fontSize: '0.75rem' } }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Typography sx={{ fontSize: '0.75rem' }}>
                                                    {option.name}
                                                    {option.components?.some(c => c.type === 'HEADER' && (c.format === 'VIDEO' || c.format === 'IMAGE')) && (
                                                        <Box component="span" sx={{ color: 'primary.main', ml: 1, fontSize: '0.65rem' }}>
                                                            (Has Media)
                                                        </Box>
                                                    )}
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        
                        {/* Header Media URL - Shows when template has header media */}
                        {requiresHeaderMedia && (
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    Header Media URL <Box component='span' sx={{ color: '#d32f2f' }}>*</Box>
                                    <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 1 }}>
                                        ({headerMediaFormat})
                                    </Box>
                                </Typography>
                                <Controller
                                    name='headerMedia.url'
                                    control={control}
                                    rules={{ 
                                        required: requiresHeaderMedia ? 'Header media URL is required' : false,
                                        pattern: {
                                            value: /^https?:\/\/.+/,
                                            message: 'Please enter a valid URL starting with http:// or https://'
                                        }
                                    }}
                                    render={({ field, fieldState: { error } }) =>
                                        <TextField
                                            {...field}
                                            size='small'
                                            fullWidth
                                            placeholder="https://example.com/media.mp4"
                                            error={!!error}
                                            helperText={error?.message}
                                            inputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    }
                                />
                            </Grid>
                        )}

                        {/* Body Field (Required - backend allowNull: false) */}
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Body <Box component='span' sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name='body'
                                control={control}
                                rules={{ 
                                    required: 'Body is required',
                                    maxLength: {
                                        value: 1000,
                                        message: 'Body must be less than 1000 characters'
                                    }
                                }}
                                render={({ field, fieldState: { error } }) =>
                                    <TextField
                                        {...field}
                                        size='small'
                                        fullWidth
                                        multiline
                                        rows={2}
                                        error={!!error}
                                        helperText={error?.message || `${field.value?.length || 0}/1000 characters`}
                                        inputProps={{ 
                                            style: { fontSize: '0.75rem' },
                                            maxLength: 1000
                                        }}
                                        onChange={(e) => {
                                            // Normalize to plain text on change
                                            field.onChange(normalizeToPlainText(e.target.value));
                                        }}
                                    />
                                }
                            />
                        </Grid>
                        
                        {/* Action Buttons */}
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                            <Button
                                variant='contained'
                                size='small'
                                color='error'
                                onClick={resetFormField}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                size='small'
                                type='submit'
                                disabled={!isDirty}
                            >
                                {selectedWhatsappEnumData ? 'Update' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </PermissionGuard>
    );
};

export default AddEditForm;
